/**
 * The pure half of {@link LetterText}: finding `[ATTORNEY: …]` markers and
 * reading a draft's markdown into blocks. No React here, so it can be tested
 * and shared (the Workbench's jump targets key on the same marker strings the
 * server's worklist does).
 */

export const ATTORNEY_MARKER = '[ATTORNEY:'

export interface DraftSegment {
  text: string
  marker: boolean
}

/**
 * The same extraction `server/app/review/worklist.py`'s `_markers()` performs,
 * so a jump target's key matches a worklist item's `detail` verbatim. A marker
 * runs to its closing bracket, or to the end of its line if it never closes.
 */
export function splitOnMarkers(draft: string): DraftSegment[] {
  const segments: DraftSegment[] = []
  let cursor = 0
  for (;;) {
    const at = draft.indexOf(ATTORNEY_MARKER, cursor)
    if (at === -1) {
      segments.push({ text: draft.slice(cursor), marker: false })
      break
    }
    if (at > cursor) segments.push({ text: draft.slice(cursor, at), marker: false })

    const close = draft.indexOf(']', at)
    const lineEnd = draft.indexOf('\n', at)
    const end =
      close === -1 || (lineEnd !== -1 && lineEnd < close)
        ? lineEnd === -1
          ? draft.length
          : lineEnd
        : close + 1
    const text = draft.slice(at, end).replace(/\s+$/, '')
    segments.push({ text, marker: true })
    cursor = Math.max(end, at + ATTORNEY_MARKER.length)
  }
  return segments
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

const HEADING = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/
const BULLET = /^\s*[-*•]\s+(.+)$/
const NUMBERED = /^\s*(\d{1,3}[.)])\s+(.+)$/
const TABLE_ROW = /^\s*\|.*\|\s*$/
const TABLE_RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/
const RULE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/
const QUOTE = /^\s*>\s?(.*)$/

export type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'bullets'; items: string[] }
  | { kind: 'numbered'; items: { num: string; text: string }[] }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'quote'; lines: string[] }

function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

/** Exported for the test: the blocks a draft is made of. */
export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = []
  let plain: string[] = []
  const flush = () => {
    if (plain.length > 0) blocks.push({ kind: 'paragraph', lines: plain })
    plain = []
  }

  const lines = text.replace(/\r\n/g, '\n').split('\n').map((line) => line.replace(/\s+$/, ''))
  let index = 0
  while (index < lines.length) {
    const line = lines[index] ?? ''
    if (!line.trim() || RULE.test(line)) {
      flush()
    } else if (TABLE_ROW.test(line)) {
      flush()
      const rows: string[][] = []
      while (index < lines.length && TABLE_ROW.test(lines[index] ?? '')) {
        const row = lines[index] ?? ''
        if (!TABLE_RULE.test(row)) rows.push(cells(row))
        index += 1
      }
      if (rows.length > 0) blocks.push({ kind: 'table', rows })
      continue
    } else if (HEADING.test(line)) {
      flush()
      const match = HEADING.exec(line)
      blocks.push({ kind: 'heading', level: match?.[1]?.length ?? 2, text: match?.[2] ?? line })
    } else if (BULLET.test(line)) {
      flush()
      const last = blocks[blocks.length - 1]
      const item = BULLET.exec(line)?.[1] ?? line
      if (last?.kind === 'bullets' && lines[index - 1]?.trim()) last.items.push(item)
      else blocks.push({ kind: 'bullets', items: [item] })
    } else if (NUMBERED.test(line)) {
      flush()
      const match = NUMBERED.exec(line)
      const entry = { num: match?.[1] ?? '', text: match?.[2] ?? line }
      const last = blocks[blocks.length - 1]
      if (last?.kind === 'numbered' && lines[index - 1]?.trim()) last.items.push(entry)
      else blocks.push({ kind: 'numbered', items: [entry] })
    } else if (QUOTE.test(line)) {
      flush()
      const quoted = QUOTE.exec(line)?.[1] ?? ''
      const last = blocks[blocks.length - 1]
      if (last?.kind === 'quote' && lines[index - 1]?.trim()) last.lines.push(quoted)
      else blocks.push({ kind: 'quote', lines: [quoted] })
    } else {
      plain.push(line)
    }
    index += 1
  }
  flush()
  return blocks
}
